import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { gql, useMutation } from '@apollo/client';
import noOp from 'utils/noop';
import { ResponseWrapper } from 'ui/toast';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';

import {
  DeleteFailure,
  DeleteSuccess,
  DeleteSnippetMutationResult,
  SnippetFailures,
  SnippetId,
} from '../types';

type Variables = {
  ids: SnippetId[];
};

export const DeleteSnippetMutation = gql`
  mutation DeleteSnippets($ids: [Long!]!) {
    deleteSnippets(ids: $ids) {
      success {
        id
        name
        __typename
      }
      failure {
        id
        __typename
      }
    }
  }
`;

type Config = {
  onComplete: () => void;
  onError: () => void;
  onSuccess: () => void;
};

export function useDeleteSnippet({ onComplete = noOp, onSuccess = noOp }: Config) {
  const dispatch = useDispatch();
  const [deleteSnippet, data] = useMutation<DeleteSnippetMutationResult, Variables>(
    DeleteSnippetMutation,
  );

  useEffect(() => {
    // don't do anything until the mutation gets called
    if (!data.called) {
      return;
    }

    if (!data.loading && data.error) {
      dispatch(addNegativeToast('An unknown error occurred'));
    }

    if (!data.loading && data?.data?.deleteSnippets) {
      const { failure, success } = data.data.deleteSnippets;

      failure.forEach((err: DeleteFailure) => {
        switch (err.__typename) {
          case SnippetFailures.DeleteFailure:
            dispatch(
              addNegativeToast(
                <ResponseWrapper>{`Snippet (id: ${err.id}) cannot be deleted. It does not exist.`}</ResponseWrapper>,
              ),
            );
            break;
          default:
            dispatch(
              addNegativeToast(
                <ResponseWrapper>{`Unable to delete snippet (id: ${err.id}).`}</ResponseWrapper>,
              ),
            );
            break;
        }
      });

      success.forEach((snippet: DeleteSuccess) => {
        dispatch(
          addPositiveToast(
            <ResponseWrapper>{`${snippet.name} was successfully deleted.`}</ResponseWrapper>,
          ),
        );
      });
      if (success.length) {
        onSuccess();
      }

      onComplete();
    }
  }, [data.loading]);

  return [deleteSnippet, data] as const;
}
